import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddUsers, ClearUserDevice, GetAllUsers, UpdateUser, getUserDetails } from '../../api/api';
import { useToast } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { normaliseText } from '../../utils/helpers';

const blankUser = () => ({
  Surname: '', Name: '', FatherHusband: '', MobileNumber: '', EmailId: '', Password: '',
  SuperAdmin: '0', AllowUserMobile: '1', IsActive: '1',
});

export const yesNo = [{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }];
export const activeOptions = [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }];

const cleanMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, 10);
export const userName = (row) => [row.Surname, row.Name].map(v => String(v || '').trim()).filter(Boolean).join(' ') || '-';
export const statusClass = (row) => row.Valid === 0 ? 'chip danger' : row.IsActive === 0 ? 'chip pend' : 'chip ok';
export const statusText = (row) => row.Valid === 0 ? 'Deleted' : row.IsActive === 0 ? 'Inactive' : 'Active';
export const deviceText = (row) => [row.MobileDeviceId && 'APK', row.DeviceName && 'Web'].filter(Boolean).join(' + ') || 'Not bound';

// Super Admin: manage every Evt_Users row. All logic here; the desktop/mobile
// files only render.
export function useUserManagement() {
  const { user, isSuperAdmin } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => blankUser());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await GetAllUsers();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(row => JSON.stringify(row).toLowerCase().includes(needle));
  }, [rows, q]);

  const setF = (key, value) => {
    const next = key === 'MobileNumber' ? cleanMobile(value)
      : ['Name', 'Surname', 'FatherHusband'].includes(key) ? normaliseText(value)
        : value;
    setForm(f => ({ ...f, [key]: next }));
  };

  const openAdd = () => { setEditing(null); setForm(blankUser()); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      Surname: row.Surname || '',
      Name: row.Name || '',
      FatherHusband: row.FatherHusband || '',
      MobileNumber: cleanMobile(row.MobileNumber),
      EmailId: row.EmailId || '',
      Password: '',
      SuperAdmin: String(row.SuperAdmin === 1 ? 1 : 0),
      AllowUserMobile: String(row.AllowUserMobile === 1 ? 1 : 0),
      IsActive: String(row.IsActive === 0 ? 0 : 1),
    });
    setOpen(true);
  };
  const close = () => setOpen(false);

  const validate = () => {
    if (!form.Name.trim()) return 'Name is required';
    if (!/^\d{10}$/.test(form.MobileNumber)) return 'Enter a valid 10-digit mobile number';
    if (!editing && !form.Password) return 'Password is required for a new user';
    if (editing?.UserId === user.UserId && Number(form.SuperAdmin) === 0) return 'You cannot remove your own Super Admin access';
    if (editing?.UserId === user.UserId && Number(form.IsActive) === 0) return 'You cannot deactivate your own user';
    return '';
  };

  const save = async () => {
    const error = validate();
    if (error) return toast(error);
    const data = {
      Surname: form.Surname.trim() || null,
      Name: form.Name.trim(),
      FatherHusband: form.FatherHusband.trim() || null,
      MobileNumber: form.MobileNumber,
      EmailId: form.EmailId.trim() || null,
      SuperAdmin: Number(form.SuperAdmin) || 0,
      AllowUserMobile: Number(form.AllowUserMobile) || 0,
      IsActive: Number(form.IsActive) || 0,
      UpdatedBy: user.UserId,
    };
    if (form.Password) data.Password = form.Password;

    setSaving(true);
    try {
      if (editing) {
        await UpdateUser({ data, where: `UserId = ${editing.UserId}` });
        toast('User updated');
      } else {
        const existing = await getUserDetails({ MobileNumber: data.MobileNumber });
        if (Array.isArray(existing) && existing.length > 0) {
          const active = existing.find(row => Number(row.Valid) !== 0);
          toast(active ? 'This mobile number is already registered' : 'This mobile number belongs to a removed user');
          return;
        }
        await AddUsers({ ...data, Valid: 1, CreatedBy: user.UserId });
        toast('User added');
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (row) => {
    if (row.UserId === user.UserId) return toast('You cannot remove your own user');
    if (!window.confirm(`Remove ${row.SuperAdmin === 1 ? 'Super Admin ' : ''}user "${userName(row)}"?`)) return;
    try {
      await UpdateUser({ data: { Valid: 0, IsActive: 0, UpdatedBy: user.UserId }, where: `UserId = ${row.UserId}` });
      toast('User removed');
      await load();
    } catch {
      toast('Remove failed');
    }
  };

  const toggleActive = async (row) => {
    if (row.UserId === user.UserId) return toast('You cannot deactivate your own user');
    const next = row.IsActive === 0 ? 1 : 0;
    if (!window.confirm(`${next ? 'Activate' : 'Deactivate'} "${userName(row)}"?`)) return;
    try {
      await UpdateUser({ data: { IsActive: next, UpdatedBy: user.UserId }, where: `UserId = ${row.UserId}` });
      toast(next ? 'User activated' : 'User deactivated');
      await load();
    } catch {
      toast('Status update failed');
    }
  };

  // Unbinds phone + browser (+ bound date/model) — the user must sign in again.
  const resetDevice = async (row) => {
    if (!window.confirm(`Reset device binding for "${userName(row)}"?`)) return;
    try {
      await ClearUserDevice({ UserId: row.UserId });
      toast('Device reset');
      await load();
    } catch {
      toast('Device reset failed');
    }
  };

  const isSelf = (row) => row.UserId === user.UserId;

  return {
    isSuperAdmin, isSelf, rows, filtered, loading, q, setQ,
    open, close, editing, form, setF, saving, openAdd, openEdit, save, softDelete, toggleActive, resetDevice,
  };
}
